const SALES = "bg-sky-50 text-sky-800 border-sky-300";
const CONTRACTING = "bg-[#F7F0E0] text-[#7A5F22] border-[#C6A55C]";
const OPERATIONS = "bg-emerald-50 text-emerald-800 border-emerald-300";

export const STATUS = {
  new: { label: "New Request", cls: SALES, group: "sales" },
  options_sent: { label: "Options Sent", cls: SALES, group: "sales" },
  booking_requested: { label: "Booking Requested", cls: CONTRACTING, group: "contracting" },
  confirmed: { label: "Confirmed", cls: CONTRACTING, group: "contracting" },
  itinerary_sent: { label: "Itinerary Sent", cls: OPERATIONS, group: "operations" },
  tracking: { label: "Flight Tracking", cls: OPERATIONS, group: "operations" },
  completed: { label: "Completed", cls: OPERATIONS, group: "operations" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-100 text-zinc-600 border-zinc-300", group: "cancelled" },
};

export const PIPELINE = ["new", "options_sent", "booking_requested", "confirmed", "itinerary_sent", "tracking", "completed"];

export const STATUS_GROUPS = [
  { key: "sales", label: "Sales", color: "#0284C7" },
  { key: "contracting", label: "Contracting", color: "#C6A55C" },
  { key: "operations", label: "Operations", color: "#059669" },
];

export const CANCEL_REASONS = {
  competitor: "Went with a competitor",
  client_changed_plans: "Client changed plans",
  price: "Price",
  no_aircraft: "No aircraft available",
  other: "Other",
};

export const CATEGORIES = [
  { value: "turboprop", label: "Turbo Prop" },
  { value: "very_light", label: "Very Light Jet" },
  { value: "light", label: "Light Jet" },
  { value: "midsize", label: "Midsize Jet" },
  { value: "super_midsize", label: "Super Midsize Jet" },
  { value: "heavy", label: "Heavy Jet" },
  { value: "ultra_long_range", label: "Ultra Long Range" },
  { value: "regional", label: "Regional Jet" },
  { value: "jumbo", label: "Jumbo Jet" },
];

export const categoryLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || "Any category";

const legCode = (a) => (a || "").split("·")[0].trim();
export const legRoute = (leg) => `${legCode(leg.from_airport)} → ${legCode(leg.to_airport)}`;
export const legsCoveredLabel = (covered, legs = []) => {
  const idx = (covered || []).filter((i) => i < legs.length);
  if (!idx.length) return "";
  return `Leg${idx.length > 1 ? "s" : ""} ${idx.map((i) => i + 1).join(", ")} only · ${idx.map((i) => legRoute(legs[i])).join(", ")}`;
};

export const fileSafe = (s = "") => s.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "Trip";

export const OFFICE_PHONE = "855-553-8738";
export const phoneLines = (u = {}) => [`Office ${OFFICE_PHONE}${u.office_ext ? ` ext. ${u.office_ext}` : ""}`, u.phone ? `Mobile ${u.phone}` : ""].filter(Boolean);

export function signatureLines(u = {}) {
  return [u.name, u.title, "JetSet Group Inc", ...phoneLines(u), u.email].filter(Boolean);
}

export const CLIENT_TYPES = [
  { value: "on_demand", label: "On Demand Charter" },
  { value: "jet_card", label: "Jet Card" },
];

export const JET_PROGRAMS = {
  preferred: { label: "Preferred", cls: "bg-[#C9A54C] text-black border-[#C9A54C] font-semibold" },
  signature: { label: "Signature", cls: "bg-zinc-300 text-black border-zinc-300 font-semibold" },
  elite: { label: "Elite", cls: "bg-black text-[#D4AF37] border-[#D4AF37] font-semibold shadow-[inset_3px_0_0_#D4AF37]" },
};

export const JET_CARD_CATEGORIES = [
  { value: "light", label: "Light" },
  { value: "midsize", label: "Mid-Size" },
  { value: "super_midsize", label: "Super Mid-Size" },
  { value: "heavy", label: "Heavy" },
];

export const TRIP_TYPES = [
  { value: "one_way", label: "One Way" },
  { value: "round_trip", label: "Round Trip" },
  { value: "multi_leg", label: "Multi-Leg" },
];

export const ROLE_LABELS = {
  admin: "Admin",
  executive: "Executive",
  flight_department: "Flight Department",
  broker: "Broker",
  pending: "Pending approval",
};

export function money(v, cur = "USD") {
  if (v === null || v === undefined) return "—";
  const cents = !Number.isInteger(Math.round(v * 100) / 100);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }).format(v);
}

export function moneyCents(v, cur = "USD") {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

export function routeText(legs = []) {
  if (!legs.length) return "";
  const short = (s = "") => s.split(" · ")[0];
  return [legs[0].from_airport, ...legs.map((l) => l.to_airport)].map(short).join(" → ");
}

const pad2 = (n) => String(n).padStart(2, "0");

export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;
}

export const fmtStamp = (iso) => (iso ? `${fmtDate(iso)} · ${new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "—");

export const fmtDateIn = (iso, tz) => new Date(iso).toLocaleDateString("en-US", { timeZone: tz, month: "2-digit", day: "2-digit", year: "numeric" }).replace(/\//g, "-");

export function shortAirport(s = "") {
  const [code, rest = ""] = s.split(" · ");
  const city = rest.endsWith(")") && rest.includes("(") ? rest.slice(rest.lastIndexOf("(") + 1, -1) : "";
  return city ? `${code.trim()} (${city})` : code.trim();
}

export const isStaff = (me) => me && ["admin", "flight_department"].includes(me.role);
export const canViewAll = (me) => isStaff(me) || me?.role === "executive";
export const viewOnlyTrip = (me, trip) => me?.role === "executive" && !!trip && trip.broker_id !== me.id;
